import { Sparkles } from 'lucide-react';

const RARITY_META = {
  common: { label: 'Milestone', color: '#FCD535', badgeText: 'COMMON', glow: 'shadow-[0_0_24px_rgba(252,213,53,0.25)]' },
  rare: { label: 'Rare', color: '#3BA0FF', badgeText: 'RARE', glow: 'shadow-[0_0_28px_rgba(59,160,255,0.35)]' },
  epic: { label: 'Epic', color: '#B26BFF', badgeText: 'EPIC', glow: 'shadow-[0_0_32px_rgba(178,107,255,0.45)]' },
  legendary: { label: 'Legendary', color: '#F6465D', badgeText: 'LEGENDARY', glow: 'shadow-[0_0_36px_rgba(246,70,93,0.55)]' },
};

function NFTArtwork({ nft, size = 128 }) {
  const art = nft.art || { palette: ['#FCD535', '#F6465D', '#0ECB81', '#181A20'], angle: 45, seed: '000000' };
  const [c1, c2, c3, c4] = art.palette;
  const angle = art.angle;
  const gradientId = `grad_${nft.nft_id}`;
  const threshold = nft.threshold_usdt || 10000;
  const label = threshold >= 1000 ? `${Math.round(threshold / 1000)}K` : String(threshold);
  return (
    <svg viewBox="0 0 200 200" width={size} height={size} className="rounded-sm">
      <defs>
        <linearGradient id={gradientId} gradientTransform={`rotate(${angle})`}>
          <stop offset="0%" stopColor={c1} />
          <stop offset="45%" stopColor={c2} />
          <stop offset="100%" stopColor={c3} />
        </linearGradient>
        <radialGradient id={`${gradientId}_orb`}>
          <stop offset="0%" stopColor={c4} stopOpacity="0" />
          <stop offset="100%" stopColor={c4} stopOpacity="0.65" />
        </radialGradient>
      </defs>
      <rect width="200" height="200" fill={`url(#${gradientId})`} />
      <circle cx="60" cy="70" r="46" fill={`url(#${gradientId}_orb)`} />
      <circle cx="140" cy="150" r="60" fill={`url(#${gradientId}_orb)`} opacity="0.7" />
      {[0, 1, 2, 3].map(i => (
        <polygon
          key={i}
          points="100,60 130,100 100,140 70,100"
          fill="none"
          stroke={c4}
          strokeOpacity={0.35 - i * 0.06}
          strokeWidth="1.5"
          transform={`rotate(${(i * 15 + (parseInt(art.seed?.slice(0, 2), 16) || 0)) % 90} 100 100) scale(${1 + i * 0.15})`}
        />
      ))}
      <text x="100" y="112" fontFamily="monospace" fontWeight="bold" fontSize="42" textAnchor="middle" fill={c4}>
        {label}
      </text>
    </svg>
  );
}

export default function MilestoneNFT({ nft, nfts, groupName }) {
  // Backward-compat: accept single `nft` prop OR a list `nfts`
  const list = nfts && nfts.length ? nfts : (nft ? [nft] : []);
  if (list.length === 0) return null;

  return (
    <div className="mt-5" data-testid="milestone-nft">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-5 h-5 text-[#FCD535]" />
        <h3 className="text-white text-lg font-semibold">Milestone NFT Collection</h3>
        <span className="text-xs uppercase tracking-wider text-[#848E9C]">{list.length} minted · rarity tiers unlock at 25K / 50K / 100K</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {list.map(n => {
          const rarity = n.rarity || 'common';
          const meta = RARITY_META[rarity] || RARITY_META.common;
          return (
            <div key={n.nft_id}
              className={`border p-3 flex items-start gap-3 bg-[#0B0E11] ${meta.glow}`}
              style={{ borderColor: meta.color + '66' }}
              data-testid={`nft-card-${n.nft_id}`}>
              <div className="flex-shrink-0" data-testid={`nft-artwork-${n.nft_id}`}>
                <NFTArtwork nft={n} size={112} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5"
                    style={{ backgroundColor: meta.color + '22', color: meta.color, border: `1px solid ${meta.color}55` }}
                    data-testid={`rarity-badge-${rarity}`}>
                    {meta.badgeText}
                  </span>
                  <span className="text-xs text-[#5E6673] font-mono">S/N {n.nft_id?.replace('nft_', '').slice(0, 8)}</span>
                </div>
                <div className="text-white font-semibold text-sm mt-1 truncate">
                  {groupName || n.group_name || 'Group'} · {n.tier_label || 'Milestone'} NFT
                </div>
                <div className="text-xs text-[#B7BDC6] mt-1.5">
                  Minted when pool crossed <span className="font-mono" style={{ color: meta.color }}>
                    {(n.threshold_usdt || 10000).toLocaleString()} USDT
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-1.5 mt-2">
                  <div className="border border-[#2B3139] px-2 py-1">
                    <div className="text-[9px] uppercase text-[#5E6673]">Pool</div>
                    <div className="font-mono text-white text-xs">{Math.round(n.pool_at_mint || 0).toLocaleString()}</div>
                  </div>
                  <div className="border border-[#2B3139] px-2 py-1">
                    <div className="text-[9px] uppercase text-[#5E6673]">Members</div>
                    <div className="font-mono text-white text-xs">{n.member_count || 0}</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
